import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Row, Col, Button, Space, Tabs, Modal } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, DeleteOutlined, PoweroffOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { ChromePicker } from 'react-color';
import Input from '../../components/input/Input';
import Select from '../../components/selects/Select';
import api from '../../services/api';
import { displayMessage } from '../../helpers/messages';
import { showSuccessMessage, showErrorMessage } from '../../services/messages';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import EBrazilStates from '../../enums/EBrazilStates';

const { confirm } = Modal;

// Componente wrapper para o ColorPicker
const ColorPickerInput = ({ value = '#1890ff', onChange }) => {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [color, setColor] = useState(value);

  const handleClick = () => {
    setDisplayColorPicker(!displayColorPicker);
  };

  const handleClose = () => {
    setDisplayColorPicker(false);
  };

  const handleChange = (newColor) => {
    setColor(newColor.hex);
    if (onChange) {
      onChange(newColor.hex);
    }
  };

  useEffect(() => {
    if (value) {
      setColor(value);
    }
  }, [value]);

  const styles = {
    color: {
      width: '100%',
      height: '40px',
      borderRadius: '4px',
      border: '1px solid #d9d9d9',
      cursor: 'pointer',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      transition: 'all 0.3s',
    },
    swatch: {
      width: '24px',
      height: '24px',
      borderRadius: '4px',
      backgroundColor: color,
      border: '1px solid #d9d9d9',
    },
    popover: {
      position: 'absolute',
      zIndex: 2,
      marginTop: '8px',
    },
    cover: {
      position: 'fixed',
      top: '0px',
      right: '0px',
      bottom: '0px',
      left: '0px',
    },
  };

  return (
    <div>
      <div style={styles.color} onClick={handleClick}>
        <span>{color}</span>
        <div style={styles.swatch} />
      </div>
      {displayColorPicker && (
        <div style={styles.popover}>
          <div style={styles.cover} onClick={handleClose} />
          <ChromePicker color={color} onChange={handleChange} />
        </div>
      )}
    </div>
  );
};

const DetailClient = () => {
  const navigate = useNavigate();
  const { idClient } = useParams();
  const { setShowLoading } = useContext(GeneralContext);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [clientData, setClientData] = useState(null);

  useEffect(() => {
    if (idClient && idClient !== 'new') {
      setIsNew(false);
      loadClient();
    } else {
      setIsNew(true);
    }
  }, [idClient]);

  const loadClient = async () => {
    setLoading(true);
    try {
      const response = await api.client.get(idClient);
      setClientData(response.data);
      form.setFieldsValue(response.data);
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_LOAD'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setLoading(true);
    try {
      if (isNew) {
        await api.client.create(values);
        showSuccessMessage(displayMessage('SUCCESS_CREATE'));
      } else {
        await api.client.update(idClient, values);
        showSuccessMessage(displayMessage('SUCCESS_UPDATE'));
      }
      navigate('/clients');
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_GENERIC'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/clients');
  };

  const handleDelete = () => {
    confirm({
      title: 'Confirmar exclusão',
      icon: <ExclamationCircleOutlined />,
      content: `Deseja realmente excluir o cliente "${clientData?.company_name}"? Esta ação não pode ser desfeita.`,
      okText: 'Sim, excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setShowLoading(true);
          await api.client.delete(idClient);
          showSuccessMessage(displayMessage('SUCCESS_DELETE'));
          navigate('/clients');
        } catch (error) {
          showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_DELETE'));
        } finally {
          setShowLoading(false);
        }
      },
    });
  };

  const handleChangeActive = () => {
    const action = clientData?.is_active ? 'inativar' : 'ativar';
    
    confirm({
      title: clientData?.is_active ? displayMessage('CONFIRM_INACTIVATE') : displayMessage('CONFIRM_ACTIVATE'),
      icon: <ExclamationCircleOutlined />,
      content: `Deseja realmente ${action} o cliente "${clientData?.company_name}"?`,
      okText: `Sim, ${action}`,
      okType: clientData?.is_active ? 'danger' : 'primary',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setShowLoading(true);
          if (clientData?.is_active) {
            await api.client.inactivate(idClient);
          } else {
            await api.client.activate(idClient);
          }
          showSuccessMessage(clientData?.is_active ? displayMessage('SUCCESS_INACTIVATE') : displayMessage('SUCCESS_ACTIVATE'));
          // Recarregar dados do cliente
          await loadClient();
        } catch (error) {
          showErrorMessage(error?.response?.data?.error || displayMessage('ERROR_GENERIC'));
        } finally {
          setShowLoading(false);
        }
      },
    });
  };

  const stateOptions = Object.values(EBrazilStates).map(state => ({
    value: state.code,
    label: `${state.code} - ${state.description}`,
  }));

  const tabItems = [
    {
      key: '1',
      label: 'Dados Gerais',
      children: (
        <>
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item
                label={displayMessage('COMPANY_NAME')}
                name="company_name"
                rules={[{ required: true, message: displayMessage('ERROR_REQUIRED_FIELD') }]}
              >
                <Input placeholder="Digite a razão social" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={displayMessage('COMPANY_DOCUMENT')}
                name="company_document"
                rules={[{ required: true, message: displayMessage('ERROR_REQUIRED_FIELD') }]}
              >
                <Input placeholder="Digite o CNPJ" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={displayMessage('NICKNAME')}
                name="nickname"
              >
                <Input placeholder="Digite o nome fantasia" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={displayMessage('SEGMENT')}
                name="segment"
              >
                <Input placeholder="Digite o segmento" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Cor Identificadora"
                name="color"
                initialValue="#1890ff"
              >
                <ColorPickerInput />
              </Form.Item>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: '2',
      label: 'Endereço',
      children: (
        <>
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item
                label={displayMessage('ADDRESS')}
                name="address"
              >
                <Input placeholder="Digite o endereço" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={displayMessage('NUMBER')}
                name="number"
              >
                <Input placeholder="Digite o número" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={displayMessage('COMPLEMENT')}
                name="complement"
              >
                <Input placeholder="Digite o complemento" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={displayMessage('NEIGHBORHOOD')}
                name="neighborhood"
              >
                <Input placeholder="Digite o bairro" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label={displayMessage('CITY')}
                name="city"
              >
                <Input placeholder="Digite a cidade" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={displayMessage('STATE')}
                name="state"
              >
                <Select
                  placeholder="Selecione o estado"
                  options={stateOptions}
                  showSearch
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                label={displayMessage('ZIP_CODE')}
                name="zip_code"
              >
                <Input placeholder="Digite o CEP" />
              </Form.Item>
            </Col>
          </Row>
        </>
      ),
    },
  ];

  return (
    <Card
      title={isNew ? displayMessage('NEW_CLIENT') : displayMessage('EDIT_CLIENT')}
      extra={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
        >
          {displayMessage('BACK')}
        </Button>
      }
    >
      <Form
        form={form}
        onFinish={handleSave}
        layout="vertical"
      >
        <Tabs defaultActiveKey="1" items={tabItems} />

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <Space>
            <Button onClick={handleBack}>
              {displayMessage('CANCEL')}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              loading={loading}
            >
              {displayMessage('SAVE')}
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* Botões de ações sensíveis - apenas para clientes existentes */}
      {!isNew && (
        <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
          <Row gutter={16}>
            {/* <Col xs={24} sm={12} style={{ textAlign: 'center', marginBottom: 16 }}>
              <Button
                type={clientData?.is_active ? 'default' : 'primary'}
                icon={<PoweroffOutlined />}
                onClick={handleChangeActive}
                style={{ minWidth: 200 }}
                size="large"
              >
                {clientData?.is_active ? 'Inativar Cliente' : 'Ativar Cliente'}
              </Button>
              <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                {clientData?.is_active 
                  ? 'Cliente não aparecerá em listagens ativas'
                  : 'Cliente voltará a aparecer em listagens'
                }
              </div>
            </Col> */}

            <Col xs={24} sm={12} style={{ textAlign: 'center', marginBottom: 16 }}>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                style={{ minWidth: 200 }}
                size="large"
              >
                Excluir Cliente Permanentemente
              </Button>
              <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                Esta ação não pode ser desfeita
              </div>
            </Col>
          </Row>
        </div>
      )}
    </Card>
  );
};

export default DetailClient;



